import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    ActivityIcon,
    ChevronRightIcon,
    ClockIcon,
    GavelIcon,
    IdCardIcon,
    ShieldAlertIcon,
    UserCogIcon,
    UserRoundIcon,
    WalletCardsIcon,
} from 'lucide-react';
import type { FcAdminPlayerCatalogData, FcAdminTarget } from '@shared/fcAdminApiTypes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabsVertical, TabsVerticalContent, TabsVerticalList, TabsVerticalTrigger } from '@/components/ui/tabs-vertical';
import { cn } from '@/lib/utils';
import { useFcAdminAccess, useFcAdminQuery } from './fcAdmin';
import { createPlayerDossierRequestGate, unwrapPlayerDossierDomain } from './playerDossierModel';
import { ActionForm, fcActionDefinitions, type ActionCatalog } from '@/pages/FcAdmin/FcAdminActionsPage';
import {
    AccessSection,
    AccountTile,
    DomainFailure,
    DossierMutationProvider,
    DossierReasonBar,
    DossierSection,
    Glare,
    InventorySection,
    ModerationComposer,
    ProfileEditors,
    SessionActions,
    VehicleSection,
    WeaponSection,
    amountText,
    money,
    nextDossierScopes,
    object,
    records,
    shouldLoadDossierCache,
    text,
    timestamp,
    useDossierMutation,
    useDossierOptionCache,
    useGlareTracking,
    type DossierCacheMode,
    type DossierOperation,
    type RecordValue,
} from './PlayerDossierParts';
import './PlayerDossier.css';

export type PlayerDossierSeed = {
    publicId: number;
    connectionGeneration?: string;
    characterRef?: string;
    name?: string;
};

type PlayerDossierContextValue = {
    openDossier: (seed: PlayerDossierSeed) => void;
    closeDossier: () => void;
};

const PlayerDossierContext = createContext<PlayerDossierContextValue | undefined>(undefined);

const normalizeTarget = (value: unknown, fallbackId?: number): FcAdminTarget | undefined => {
    const source = object(value);
    const publicId = Number(source.publicId ?? source.playerId ?? fallbackId);
    if (!Number.isSafeInteger(publicId) || publicId < 0) return undefined;
    const connectionGeneration = typeof source.connectionGeneration === 'string' ? source.connectionGeneration : undefined;
    const characterRef = typeof source.characterRef === 'string' ? source.characterRef : undefined;
    if (connectionGeneration) return { publicId, connectionGeneration };
    if (characterRef) return { publicId, characterRef };
    return { publicId };
};

const targetKey = (target?: FcAdminTarget) => target
    ? `${target.publicId}:${target.connectionGeneration ?? target.characterRef ?? ''}`
    : '';

const playerName = (header: RecordValue, fallback?: string) => text(header.rpName ?? header.name, fallback || 'Spielerakte');

type DossierDomain = 'header' | 'ownership' | 'moderation' | 'administration';

type DossierLoad = { request: number; scope: string; revision: string; target: FcAdminTarget };

type HeaderSnapshot = { scope: string; revision: string; data: unknown };

const dossierDomains: DossierDomain[] = ['header', 'ownership', 'moderation', 'administration'];

const mutationDomains: Record<DossierOperation, DossierDomain[]> = {
    'player.money': ['ownership'],
    'player.coins': ['ownership'],
    'player.item': ['ownership'],
    'player.weapon': ['ownership'],
    'vehicle.give': ['ownership'],
    'vehicle.impound': ['ownership'],
    'vehicle.delete': ['ownership'],
    'vip.update': ['ownership'],
    'licenses.update': ['ownership'],
    'moderation.ban': ['moderation', 'header'],
    'moderation.warn': ['moderation', 'header'],
    'moderation.note': ['moderation', 'header'],
    'moderation.revoke': ['moderation', 'header'],
    'player.job': ['administration', 'header'],
    'player.group': ['administration', 'header'],
    'player.status': ['administration', 'header'],
    'player.dimension': ['administration', 'header'],
    'jail.apply': ['administration', 'header'],
    'jail.release': ['administration', 'header'],
    'player.freeze': ['administration', 'header'],
    'player.heal': ['administration', 'header'],
    'player.revive': ['administration', 'header'],
    'player.kick': ['administration', 'header'],
};

function LoadingBlock({ label }: { label: string }) {
    return (
        <div className="fc-dossier-loading" aria-live="polite">
            <span /><span /><span />
            <p>{label}</p>
        </div>
    );
}

const partialLabels: Record<string, string> = {
    accounts: 'Konten',
    economy: 'Konten',
    coins: 'Coins',
    inventory: 'Inventar',
    weapons: 'UID-Waffen',
    uidWeapons: 'UID-Waffen',
    vehicles: 'Fahrzeuge',
    vip: 'VIP',
    licenses: 'Lizenzen',
};

const unavailableValue = (unavailable: RecordValue, ...keys: string[]) => {
    const key = keys.find((entry) => unavailable[entry] !== undefined);
    return key ? unavailable[key] : undefined;
};

function PartialFailure({ domain, value, onRetry }: { domain: string; value: unknown; onRetry: () => void }) {
    if (value === undefined) return null;
    return <DomainFailure message={`${partialLabels[domain] ?? domain} konnte nicht geladen werden. Die übrigen Aktenbereiche bleiben verfügbar.`} onRetry={onRetry} />;
}

function Facts({ values }: { values: [string, unknown, string?][] }) {
    return (
        <dl className="fc-dossier-facts">
            {values.map(([label, value, tone]) => <div key={label}><dt>{label}</dt><dd className={tone}>{text(value)}</dd></div>)}
        </dl>
    );
}

function DisabilityForm({ target, data, onSuccess }: { target: FcAdminTarget; data: RecordValue; onSuccess: () => void }) {
    const { mutationReady, status } = useFcAdminAccess();
    const definition = useMemo(() => {
        const entry = fcActionDefinitions.find((candidate) => candidate.operation === 'licenses.update');
        return entry ? { ...entry, optionsOperation: undefined } : undefined;
    }, []);
    if (!definition) return null;
    return (
        <div className="fc-dossier-legacy-form">
            <ActionForm
                key={`disability:${targetKey(target)}`}
                definition={definition}
                disabled={!mutationReady}
                groupOptions={status?.groupOptions ?? []}
                lockedTarget={target}
                currentValues={data}
                presetFields={{ action: 'setDisability' }}
                hiddenFields={['action']}
                submitLabel="Ausweis speichern"
                onSuccess={onSuccess}
            />
        </div>
    );
}

function History({ entries, kind, allowRevoke = true }: {
    entries: RecordValue[];
    kind: 'ban' | 'warn' | 'note';
    allowRevoke?: boolean;
}) {
    const { capabilities, status } = useFcAdminAccess();
    const { mutate, ready, busy } = useDossierMutation();
    const permissions = new Set(status?.actor?.permissions ?? []);
    const permitted = allowRevoke && capabilities.has('moderation.revoke') && (
        kind === 'ban' ? permissions.has('unban') : kind === 'warn' ? permissions.has('warn_manage') : permissions.has('notes')
    );
    if (!entries.length) return <div className="fc-dossier-empty">Keine Einträge vorhanden.</div>;
    return (
        <div className="fc-dossier-history">
            {entries.map((entry, index) => {
                const revokedAt = entry.revokedAt ?? entry.revoked_at ?? entry.unbannedAt ?? entry.unbanned_at;
                const revokedBy = entry.revokedByName ?? entry.revoked_by_name ?? entry.revokedBy ?? entry.revoked_by ?? entry.unbannedBy;
                const expiresAt = entry.expiresAt ?? entry.expires;
                const expiresNumeric = Number(expiresAt);
                const expiresDate = Number.isFinite(expiresNumeric)
                    ? expiresNumeric < 10_000_000_000 ? expiresNumeric * 1000 : expiresNumeric
                    : new Date(String(expiresAt ?? '')).getTime();
                const expired = kind === 'ban' && !!expiresAt && Number.isFinite(expiresDate) && expiresDate <= Date.now();
                const active = !revokedAt && !expired;
                const status = revokedAt ? 'Widerrufen' : expired ? 'Abgelaufen' : 'Aktiv';
                const rowKey = String(entry.id ?? entry.sourceId ?? index);
                const sourceSystem = entry.sourceSystem ?? entry.source_system;
                const sourceId = entry.sourceId ?? entry.source_id;
                const sourceScope = entry.sourceScope ?? entry.source_scope;
                const presetFields: Record<string, string | number | boolean> = {
                    kind,
                    id: String(entry.id ?? sourceId ?? ''),
                    ...(kind === 'ban' ? { banId: String(entry.id ?? '') } : {}),
                    ...(typeof sourceSystem === 'string' ? { sourceSystem } : {}),
                    ...(typeof sourceId === 'string' ? { sourceId } : {}),
                    ...(typeof sourceScope === 'string' ? { sourceScope } : {}),
                };
                const label = kind === 'ban' ? 'Ban' : kind === 'warn' ? 'Verwarnung' : 'Notiz';
                const headline = text(entry.reason ?? entry.note, kind === 'note' ? 'Notiz' : 'Kein Grund hinterlegt');
                return (
                    <article key={rowKey} className={cn('fc-dossier-entry', `is-${kind}`, active ? 'is-active' : 'is-closed')}>
                        <header>
                            <strong>{headline}</strong>
                            <span className={cn('fc-pill', active ? kind === 'ban' ? 'danger' : kind === 'warn' ? 'warn' : 'accent' : undefined)}>{status}</span>
                            {permitted && active && presetFields.id ? (
                                <Button type="button" size="xs" variant="ghost-destructive" disabled={!ready || busy} onClick={() => void mutate({
                                    operation: 'moderation.revoke',
                                    payload: presetFields,
                                    title: `${label} widerrufen`,
                                    before: `${label} · aktiv`,
                                    after: `${label} · widerrufen`,
                                    danger: true,
                                })}>Widerrufen</Button>
                            ) : null}
                        </header>
                        <dl>
                            <div><dt>Von</dt><dd>{text(entry.adminName ?? entry.admin_name ?? entry.administrator, 'System')}</dd></div>
                            <div><dt>Am</dt><dd>{timestamp(entry.createdAt ?? entry.created_at ?? entry.at)}</dd></div>
                            {expiresAt !== undefined ? <div><dt>Gültig bis</dt><dd>{timestamp(expiresAt)}</dd></div> : null}
                            {revokedAt ? <div><dt>Widerrufen von</dt><dd>{text(revokedBy, 'System')}</dd></div> : null}
                            {revokedAt ? <div><dt>Widerrufen am</dt><dd>{timestamp(revokedAt)}</dd></div> : null}
                            {revokedAt && (entry.revokeReason ?? entry.revoke_reason) ? <div><dt>Widerrufsgrund</dt><dd>{text(entry.revokeReason ?? entry.revoke_reason)}</dd></div> : null}
                        </dl>
                    </article>
                );
            })}
        </div>
    );
}

const entryTime = (entry: RecordValue) => {
    const value = entry.createdAt ?? entry.created_at ?? entry.at;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric < 10_000_000_000 ? numeric * 1000 : numeric;
    const parsed = new Date(String(value ?? '')).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

function Overview({ header, ownership, moderation, profile, onNavigate }: { header: RecordValue; ownership: RecordValue; moderation: RecordValue; profile: RecordValue; onNavigate: (tab: string) => void }) {
    const accounts = object(ownership.accounts);
    const history = Object.keys(object(moderation.history)).length ? object(moderation.history) : moderation;
    const activeBans = records(history.bans).filter((entry) => !(entry.revokedAt ?? entry.revoked_at ?? entry.unbannedAt)).length;
    const activeWarns = records(history.warns).filter((entry) => !(entry.revokedAt ?? entry.revoked_at)).length;
    const notes = records(history.notes).length;
    const online = header.online === true;
    const hasMoney = Object.keys(accounts).length > 0;
    const vip = object(ownership.vip);
    const vipActive = vip.active === undefined ? !!vip.tier && vip.tier !== 'none' : vip.active === true;
    const licenses = records(ownership.licenses);
    const recent = [
        ...records(history.bans).map((entry) => ({ entry, kind: 'ban' as const })),
        ...records(history.warns).map((entry) => ({ entry, kind: 'warn' as const })),
        ...records(history.notes).map((entry) => ({ entry, kind: 'note' as const })),
    ].sort((left, right) => entryTime(right.entry) - entryTime(left.entry)).slice(0, 3);
    return (
        <div className="fc-dossier-overview">
            <div className="fc-dossier-kpis">
                <Glare className="fc-stat-card"><span className="fc-stat-card-label"><WalletCardsIcon />Bargeld</span><span className="fc-stat-card-value">{hasMoney ? money(accounts.money) : '–'}</span><span className="fc-stat-card-sub">Bank {hasMoney ? money(accounts.bank) : '–'}</span></Glare>
                <Glare className="fc-stat-card"><span className="fc-stat-card-label"><IdCardIcon />Coins</span><span className="fc-stat-card-value">{ownership.coins !== undefined ? amountText(ownership.coins) : '–'}</span><span className="fc-stat-card-sub">{records(ownership.vehicles).length} Fahrzeuge · {records(ownership.inventory).length} Items</span></Glare>
                <Glare className="fc-stat-card"><span className="fc-stat-card-label"><ClockIcon />Spielzeit</span><span className="fc-stat-card-value">{text(header.playtimeLabel ?? header.playtime)}</span><span className="fc-stat-card-sub">Zuletzt {timestamp(header.lastSeen)}</span></Glare>
                <Glare className="fc-stat-card"><span className="fc-stat-card-label"><GavelIcon />Moderation</span><span className="fc-stat-card-value">{activeBans + activeWarns}</span><span className="fc-stat-card-sub">{activeBans} Bans · {activeWarns} Warns · {notes} Notizen</span></Glare>
            </div>
            <div className="fc-dossier-direct-grid">
                <DossierSection icon={<UserRoundIcon />} title="Profil">
                    <Facts values={[
                        ['FC-ID', header.publicId ?? header.playerId],
                        ['Name', header.rpName ?? header.name],
                        ['Gruppe', profile.groupLabel ?? header.groupLabel ?? header.group],
                        ['Job', profile.jobLabel ?? header.jobLabel ?? header.jobName ?? header.job],
                        ['Jobrang', profile.jobGradeLabel ?? header.jobGradeLabel ?? header.jobGrade],
                        ['Spielzeit', header.playtimeLabel ?? header.playtime],
                    ]} />
                </DossierSection>
                <DossierSection icon={<ActivityIcon />} title="Sitzung" meta={online ? 'Online' : 'Offline'}>
                    <Facts values={[
                        ['Status', online ? 'Online' : 'Offline', online ? 'is-good' : undefined],
                        ['Verbindung', online ? `${text(header.ping, '0')} ms` : 'Keine aktive Sitzung'],
                        ['Dimension', profile.bucket ?? header.bucket],
                        ['Admin-Dienst', profile.aduty === true || header.aduty === true ? 'Aktiv' : 'Inaktiv'],
                        ['Freeze', profile.frozen === true || header.frozen === true ? 'Aktiv' : 'Inaktiv', profile.frozen === true ? 'is-warn' : undefined],
                        ['Zuletzt gesehen', timestamp(header.lastSeen)],
                    ]} />
                </DossierSection>
            </div>
            <div className="fc-dossier-direct-grid">
                <DossierSection icon={<IdCardIcon />} title="Zugänge" meta={vipActive ? `VIP ${text(vip.label ?? vip.tier)}` : 'Kein VIP'} actions={<button type="button" className="fc-dossier-link" onClick={() => onNavigate('ownership')}>Bearbeiten<ChevronRightIcon /></button>}>
                    <div className="fc-dossier-license-list">
                        {vipActive ? <span className="fc-dossier-license">VIP {text(vip.label ?? vip.tier)}</span> : null}
                        {licenses.map((license, index) => <span key={text(license.type, String(index))} className={cn('fc-dossier-license', license.active === false && 'is-inactive')}>{text(license.label ?? license.type)}</span>)}
                        {!vipActive && !licenses.length ? <span className="fc-dossier-muted">Keine Zugänge hinterlegt.</span> : null}
                    </div>
                </DossierSection>
                <DossierSection icon={<GavelIcon />} title="Letzte Einträge" meta={`${records(history.bans).length + records(history.warns).length + notes} gesamt`} actions={<button type="button" className="fc-dossier-link" onClick={() => onNavigate('moderation')}>Verlauf<ChevronRightIcon /></button>}>
                    {recent.length ? (
                        <div className="fc-dossier-recent">
                            {recent.map(({ entry, kind }, index) => {
                                const closed = !!(entry.revokedAt ?? entry.revoked_at ?? entry.unbannedAt);
                                return (
                                    <div key={`${kind}:${text(entry.id, String(index))}`} className={cn('fc-dossier-recent-row', closed && 'is-closed')}>
                                        <span className={cn('fc-pill', closed ? undefined : kind === 'ban' ? 'danger' : kind === 'warn' ? 'warn' : 'accent')}>{kind === 'ban' ? 'Ban' : kind === 'warn' ? 'Warn' : 'Notiz'}</span>
                                        <strong>{text(entry.reason ?? entry.note ?? entry.body, 'Ohne Text')}</strong>
                                        <small>{timestamp(entry.createdAt ?? entry.created_at ?? entry.at)}</small>
                                    </div>
                                );
                            })}
                        </div>
                    ) : <div className="fc-dossier-muted">Keine Akteneinträge vorhanden.</div>}
                </DossierSection>
            </div>
            {records(header.identifiers).length > 0 ? (
                <DossierSection icon={<IdCardIcon />} title="Freigegebene Identifier">
                    <div className="fc-dossier-identifiers">{records(header.identifiers).map((entry) => <span key={text(entry.type)}><b>{text(entry.type)}</b><code>{text(entry.value)}</code></span>)}</div>
                </DossierSection>
            ) : null}
        </div>
    );
}

function Ownership({ data, target, catalog, onRetry, onSuccess }: { data: RecordValue; target: FcAdminTarget; catalog: ActionCatalog; onRetry: () => void; onSuccess: () => void }) {
    const accounts = object(data.accounts);
    const unavailable = object(data.unavailable);
    const accountsUnavailable = unavailableValue(unavailable, 'accounts', 'economy');
    const coinsUnavailable = unavailableValue(unavailable, 'coins');
    const inventoryUnavailable = unavailableValue(unavailable, 'inventory');
    const weaponsUnavailable = unavailableValue(unavailable, 'weapons', 'uidWeapons');
    const vehiclesUnavailable = unavailableValue(unavailable, 'vehicles');
    const vipUnavailable = unavailableValue(unavailable, 'vip');
    const licensesUnavailable = unavailableValue(unavailable, 'licenses');
    return (
        <div className="fc-dossier-ownership">
            <PartialFailure domain="accounts" value={accountsUnavailable} onRetry={onRetry} />
            <PartialFailure domain="coins" value={coinsUnavailable} onRetry={onRetry} />
            <div className="fc-dossier-money">
                {accountsUnavailable === undefined ? <>
                    <AccountTile account="money" value={accounts.money} />
                    <AccountTile account="bank" value={accounts.bank} />
                    <AccountTile account="black_money" value={accounts.black_money} />
                </> : null}
                {coinsUnavailable === undefined ? <AccountTile account="coins" value={data.coins ?? accounts.coins} coins /> : null}
            </div>
            <div className="fc-dossier-direct-grid">
                {inventoryUnavailable !== undefined ? <PartialFailure domain="inventory" value={inventoryUnavailable} onRetry={onRetry} /> : <InventorySection data={data} catalog={catalog} />}
                {weaponsUnavailable !== undefined ? <PartialFailure domain="weapons" value={weaponsUnavailable} onRetry={onRetry} /> : <WeaponSection data={data} catalog={catalog} />}
                {vehiclesUnavailable !== undefined ? <PartialFailure domain="vehicles" value={vehiclesUnavailable} onRetry={onRetry} /> : <VehicleSection data={data} catalog={catalog} />}
                {vipUnavailable !== undefined || licensesUnavailable !== undefined
                    ? <PartialFailure domain={vipUnavailable !== undefined ? 'vip' : 'licenses'} value={vipUnavailable ?? licensesUnavailable} onRetry={onRetry} />
                    : <AccessSection data={data} disabilityForm={<DisabilityForm target={target} data={data} onSuccess={onSuccess} />} />}
            </div>
        </div>
    );
}

function Moderation({ data }: { data: RecordValue }) {
    const history = Object.keys(object(data.history)).length ? object(data.history) : data;
    const bans = records(history.bans);
    const warns = records(history.warns);
    const notes = records(history.notes);
    const jail = records(history.jail ?? history.jails);
    const initialTab = bans.length ? 'bans' : warns.length ? 'warns' : notes.length ? 'notes' : jail.length ? 'jail' : 'bans';
    return (
        <div className="fc-dossier-moderation">
            <ModerationComposer />
            <Tabs defaultValue={initialTab}>
                <TabsList className="fc-dossier-subtabs">
                    <TabsTrigger value="bans">Bans <em>{bans.length}</em></TabsTrigger>
                    <TabsTrigger value="warns">Warns <em>{warns.length}</em></TabsTrigger>
                    <TabsTrigger value="notes">Notizen <em>{notes.length}</em></TabsTrigger>
                    <TabsTrigger value="jail">Jail <em>{jail.length}</em></TabsTrigger>
                </TabsList>
                <TabsContent value="bans"><History entries={bans} kind="ban" /></TabsContent>
                <TabsContent value="warns"><History entries={warns} kind="warn" /></TabsContent>
                <TabsContent value="notes"><History entries={notes} kind="note" /></TabsContent>
                <TabsContent value="jail"><History entries={jail} kind="note" allowRevoke={false} /></TabsContent>
            </Tabs>
        </div>
    );
}

function Administration({ data, catalog }: { data: RecordValue; catalog: ActionCatalog }) {
    const profile = Object.keys(object(data.profile)).length ? object(data.profile) : data;
    return (
        <div className="fc-dossier-administration">
            <Facts values={[
                ['Gruppe', profile.groupLabel ?? profile.group],
                ['Job', profile.jobLabel ?? profile.jobName ?? profile.job],
                ['Jobrang', profile.jobGradeLabel ?? profile.jobGrade],
                ['Dimension', profile.bucket],
                ['Admin-Dienst', profile.aduty === true ? 'Aktiv' : 'Inaktiv'],
                ['Freeze', profile.frozen === true ? 'Aktiv' : 'Inaktiv'],
            ]} />
            <ProfileEditors profile={profile} catalog={catalog} />
        </div>
    );
}

export function PlayerDossierProvider({ children }: { children: ReactNode }) {
    const headerQuery = useFcAdminQuery('players.get');
    const ownershipQuery = useFcAdminQuery('players.domain');
    const moderationQuery = useFcAdminQuery('players.domain');
    const administrationQuery = useFcAdminQuery('players.domain');
    const catalogQuery = useFcAdminQuery<FcAdminPlayerCatalogData>('players.catalog');
    const loadHeader = headerQuery.run;
    const loadOwnership = ownershipQuery.run;
    const loadModeration = moderationQuery.run;
    const loadAdministration = administrationQuery.run;
    const loadCatalog = catalogQuery.run;
    const { authorized, accessRevision } = useFcAdminAccess();
    const [seed, setSeed] = useState<PlayerDossierSeed>();
    const [target, setTarget] = useState<FcAdminTarget>();
    const [activeTab, setActiveTab] = useState('overview');
    const [resolveError, setResolveError] = useState<string>();
    const [loadedScopes, setLoadedScopes] = useState<Partial<Record<DossierDomain, string>>>({});
    const [headerSnapshot, setHeaderSnapshot] = useState<HeaderSnapshot>();
    const [dataRevision, setDataRevision] = useState(0);
    const requestGate = useRef(createPlayerDossierRequestGate());
    const activeLoad = useRef<DossierLoad>();
    const domainSequence = useRef<Record<DossierDomain, number>>({ header: 0, ownership: 0, moderation: 0, administration: 0 });
    const catalogRequest = useRef<{ publicId: number; run: typeof loadCatalog; failed: boolean }>();
    const glareRef = useGlareTracking<HTMLDivElement>();
    const dossierOptions = useDossierOptionCache();
    const invalidateTemplates = dossierOptions.templates.invalidate;
    const seedPublicId = seed?.publicId;

    const closeDossier = useCallback(() => {
        requestGate.current.invalidate();
        setSeed(undefined);
        setTarget(undefined);
        setResolveError(undefined);
        setLoadedScopes({});
        setHeaderSnapshot(undefined);
    }, []);

    const openDossier = useCallback((next: PlayerDossierSeed) => {
        requestGate.current.invalidate();
        if (catalogRequest.current?.failed) catalogRequest.current = undefined;
        invalidateTemplates();
        setSeed(next);
        setResolveError(undefined);
        setLoadedScopes({});
        setHeaderSnapshot(undefined);
        setActiveTab('overview');
        const direct = normalizeTarget(next, next.publicId);
        setTarget(direct);
    }, [invalidateTemplates]);

    useEffect(() => {
        if (!seed || !target || !authorized || target.connectionGeneration || target.characterRef) return;
        const request = requestGate.current.begin();
        setLoadedScopes({});
        void loadHeader({ target }).then((response) => {
            if (!requestGate.current.accepts(request)) return;
            if (!response?.ok) {
                setResolveError(response?.error ?? 'Die aktive Spielerakte konnte nicht aufgelöst werden.');
                return;
            }
            const item = records(response.data.items)[0] ?? {};
            const resolved = normalizeTarget(item.target, seed.publicId);
            if (!resolved || (!resolved.connectionGeneration && !resolved.characterRef)) {
                setResolveError('Für diese FC-ID wurde keine eindeutige aktive Spielerakte gefunden.');
                return;
            }
            setTarget(resolved);
        });
    }, [authorized, dataRevision, loadHeader, seed, target]);

    const loadDomain = useCallback((domain: DossierDomain, { request, scope, revision, target: domainTarget }: DossierLoad, partial = false) => {
        const sequence = ++domainSequence.current[domain];
        const markLoaded = (response: { ok: true; data: unknown } | { ok: false } | undefined) => {
            if (!requestGate.current.accepts(request) || domainSequence.current[domain] !== sequence) return;
            if (domain === 'header' && response?.ok) setHeaderSnapshot({ scope, revision, data: response.data });
            setLoadedScopes((current) => nextDossierScopes(current, domain, scope, !!response?.ok, partial && domain === 'header'));
        };
        const response = domain === 'header'
            ? loadHeader({ target: domainTarget })
            : (domain === 'ownership' ? loadOwnership : domain === 'moderation' ? loadModeration : loadAdministration)({ target: domainTarget, filters: { domain } });
        void response.then(markLoaded);
    }, [loadAdministration, loadHeader, loadModeration, loadOwnership]);

    useEffect(() => {
        if (!seed || !target || !authorized || (!target.connectionGeneration && !target.characterRef)) return;
        const load: DossierLoad = { request: requestGate.current.begin(), scope: targetKey(target), revision: accessRevision, target };
        activeLoad.current = load;
        setLoadedScopes({});
        for (const domain of dossierDomains) loadDomain(domain, load);
    }, [accessRevision, authorized, dataRevision, loadDomain, seed, target]);

    const reloadDomains = useCallback((domains: DossierDomain[]) => {
        const load = activeLoad.current;
        if (!load || !requestGate.current.accepts(load.request)) return;
        for (const domain of domains) loadDomain(domain, load, true);
    }, [loadDomain]);
    const reloadAfterMutation = useCallback((operation: DossierOperation) => reloadDomains(mutationDomains[operation]), [reloadDomains]);
    const retryHeader = useCallback(() => reloadDomains(['header']), [reloadDomains]);

    const ensureCatalog = useCallback((mode: DossierCacheMode) => {
        if (seedPublicId === undefined || !authorized) return;
        const current = catalogRequest.current?.publicId === seedPublicId && catalogRequest.current.run === loadCatalog ? catalogRequest.current : undefined;
        if (!shouldLoadDossierCache(current, mode)) return;
        const entry = { publicId: seedPublicId, run: loadCatalog, failed: false };
        catalogRequest.current = entry;
        void loadCatalog().then((response) => { entry.failed = !response?.ok; });
    }, [authorized, loadCatalog, seedPublicId]);
    const requireCatalog = useCallback(() => ensureCatalog('ensure'), [ensureCatalog]);
    const retryCatalog = useCallback(() => ensureCatalog('retry'), [ensureCatalog]);
    const catalogNeeded = activeTab === 'ownership' || activeTab === 'administration';
    const domainsLoading = headerQuery.loading || ownershipQuery.loading || moderationQuery.loading || administrationQuery.loading;

    useEffect(() => {
        if (catalogNeeded && !domainsLoading) requireCatalog();
    }, [catalogNeeded, domainsLoading, requireCatalog]);

    const activeScope = targetKey(target);
    const headerCurrent = loadedScopes.header === activeScope && headerSnapshot?.scope === activeScope && headerSnapshot.revision === accessRevision;
    const headerItem = headerCurrent ? records(object(headerSnapshot.data).items)[0] ?? {} : {};
    const header = Object.keys(object(headerItem.header)).length ? object(headerItem.header) : headerItem;
    const characters = records(headerItem.characters);
    const ownershipDomain = loadedScopes.ownership === activeScope ? unwrapPlayerDossierDomain(ownershipQuery.data, 'ownership') : {};
    const moderation = loadedScopes.moderation === activeScope ? unwrapPlayerDossierDomain(moderationQuery.data, 'moderation') : {};
    const administration = loadedScopes.administration === activeScope ? unwrapPlayerDossierDomain(administrationQuery.data, 'administration') : {};
    const profile = Object.keys(object(administration.profile)).length ? object(administration.profile) : administration;
    const ownership: RecordValue = {
        ...ownershipDomain,
        ...(ownershipDomain.vip === undefined && administration.vip !== undefined ? { vip: administration.vip } : {}),
        ...(ownershipDomain.licenses === undefined && administration.licenses !== undefined ? { licenses: administration.licenses } : {}),
    };
    const selectedTarget = normalizeTarget(headerItem.target, target?.publicId) ?? target;
    const catalog: ActionCatalog = {
        items: catalogQuery.data?.items ?? [],
        weapons: catalogQuery.data?.weapons ?? [],
        vehicles: catalogQuery.data?.vehicles ?? [],
        jobs: catalogQuery.data?.jobs ?? [],
    };
    const title = playerName(header, seed?.name);
    const context = useMemo(() => ({ openDossier, closeDossier }), [closeDossier, openDossier]);
    const retryAll = useCallback(() => {
        setResolveError(undefined);
        setLoadedScopes({});
        setDataRevision((current) => current + 1);
        ensureCatalog('repair');
    }, [ensureCatalog]);
    const reloadDisability = useCallback(() => reloadAfterMutation('licenses.update'), [reloadAfterMutation]);
    const online = header.online === true || !!selectedTarget?.connectionGeneration;
    const historyData = Object.keys(object(moderation.history)).length ? object(moderation.history) : moderation;
    const moderationCount = records(historyData.bans).length + records(historyData.warns).length + records(historyData.notes).length;
    const vip = object(ownership.vip);
    const vipActive = vip.active === undefined ? !!vip.tier && vip.tier !== 'none' : vip.active === true;
    const groupLabel = text(profile.groupLabel ?? header.groupLabel ?? header.group, '');
    const jobLabel = text(profile.jobLabel ?? header.jobLabel ?? header.jobName ?? header.job, '');
    const gradeLabel = text(profile.jobGradeLabel ?? header.jobGradeLabel ?? profile.jobGrade ?? header.jobGrade, '');
    const characterName = (character: RecordValue, index: number) => text(character.rpName ?? character.name, `Charakter ${index + 1}`);
    const selectCharacter = (value: string) => {
        const character = characters.find((entry) => targetKey(normalizeTarget(entry.target ?? entry, seed?.publicId)) === value);
        const next = normalizeTarget(character?.target ?? character, seed?.publicId);
        if (next) setTarget(next);
    };
    const ready = !!selectedTarget && headerCurrent;

    return (
        <PlayerDossierContext.Provider value={context}>
            {children}
            <Dialog open={authorized && seed !== undefined} onOpenChange={(open) => { if (!open) closeDossier(); }}>
                <DialogContent ref={glareRef} className="fc-dossier-dialog">
                    <span className="fc-panel-accent" />
                    <span className="fc-panel-stripes" />
                    <span className="fc-panel-corner tl" />
                    <span className="fc-panel-corner br" />
                    <UserRoundIcon className="fc-panel-watermark" strokeWidth={1.2} aria-hidden="true" />
                    <header className="fc-dossier-hero">
                        <div className="fc-dossier-idblock">
                            <small>FC-ID</small>
                            <strong>{seed?.publicId ?? '–'}</strong>
                        </div>
                        <div className="fc-dossier-identity">
                            <span className="fc-kicker">Dossier</span>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>
                                {[jobLabel, gradeLabel ? `Rang ${gradeLabel}` : '', groupLabel].filter(Boolean).join(' · ') || `Permanente FC-ID ${seed?.publicId ?? '–'}`}
                            </DialogDescription>
                            <div className="fc-dossier-badges">
                                <span className={cn('fc-pill', online ? 'good' : undefined)}><i className={cn('fc-dossier-dot', online && 'is-online')} />{online ? 'Online' : 'Offline'}</span>
                                {profile.aduty === true || header.aduty === true ? <span className="fc-pill accent">Admin-Dienst</span> : null}
                                {profile.frozen === true || header.frozen === true ? <span className="fc-pill warn">Eingefroren</span> : null}
                                {vipActive ? <span className="fc-pill accent">VIP {text(vip.label ?? vip.tier)}</span> : null}
                                {online && header.ping !== undefined ? <span className="fc-pill">{text(header.ping)} ms</span> : null}
                            </div>
                        </div>
                        <div className="fc-dossier-hero-side">
                            {characters.length > 1 ? (
                                characters.length <= 3 ? (
                                    <div className="fc-segment fc-dossier-characters" role="tablist" aria-label="Charakter wählen">
                                        {characters.map((character, index) => {
                                            const optionTarget = normalizeTarget(character.target ?? character, seed?.publicId);
                                            if (!optionTarget) return null;
                                            const key = targetKey(optionTarget);
                                            return <button key={key} type="button" className={cn(key === targetKey(selectedTarget) && 'active')} onClick={() => selectCharacter(key)}>{characterName(character, index)}</button>;
                                        })}
                                    </div>
                                ) : (
                                    <Select value={targetKey(selectedTarget)} onValueChange={selectCharacter}>
                                        <SelectTrigger className="w-64"><SelectValue placeholder="Charakter wählen" /></SelectTrigger>
                                        <SelectContent>{characters.map((character, index) => {
                                            const optionTarget = normalizeTarget(character.target ?? character, seed?.publicId);
                                            if (!optionTarget) return null;
                                            return <SelectItem key={targetKey(optionTarget)} value={targetKey(optionTarget)}>{characterName(character, index)}</SelectItem>;
                                        })}</SelectContent>
                                    </Select>
                                )
                            ) : null}
                        </div>
                    </header>
                    {resolveError ? <div className="fc-dossier-stage"><DomainFailure message={resolveError} onRetry={retryAll} /></div> : !ready && headerQuery.loading || !target ? <div className="fc-dossier-stage"><LoadingBlock label="Spielerakte wird geladen …" /></div> : !ready && headerQuery.error ? <div className="fc-dossier-stage"><DomainFailure message={headerQuery.error} onRetry={retryAll} /></div> : selectedTarget ? (
                        <DossierMutationProvider key={targetKey(selectedTarget)} target={selectedTarget} targetLabel={title} options={dossierOptions} onSuccess={reloadAfterMutation}>
                            <div className="fc-dossier-toolbar">
                                <DossierReasonBar />
                                <SessionActions header={header} profile={profile} />
                                {headerQuery.error ? <div className="basis-full [&>.fc-dossier-domain-error]:mb-0"><DomainFailure message={headerQuery.error} onRetry={retryHeader} /></div> : null}
                            </div>
                            <TabsVertical value={activeTab} onValueChange={setActiveTab} className="fc-dossier-tabs">
                                <TabsVerticalList className="fc-dossier-tablist">
                                    <span className="fc-kicker">Bereiche</span>
                                    <TabsVerticalTrigger value="overview"><ActivityIcon /><span>Überblick</span></TabsVerticalTrigger>
                                    <TabsVerticalTrigger value="ownership"><WalletCardsIcon /><span>Besitz</span><em>{records(ownership.inventory).length + records(ownership.vehicles).length || ''}</em></TabsVerticalTrigger>
                                    <TabsVerticalTrigger value="moderation"><ShieldAlertIcon /><span>Moderation</span><em>{moderationCount || ''}</em></TabsVerticalTrigger>
                                    <TabsVerticalTrigger value="administration"><UserCogIcon /><span>Verwaltung</span></TabsVerticalTrigger>
                                    <div className="fc-dossier-tablist-foot">
                                        <span>Zuletzt gesehen</span>
                                        <b>{timestamp(header.lastSeen)}</b>
                                    </div>
                                </TabsVerticalList>
                                <div className="fc-dossier-content">
                                    <TabsVerticalContent value="overview" className="fc-dossier-panel">
                                        <div className="fc-dossier-workspace is-direct"><main className="fc-dossier-primary" tabIndex={0} aria-label="Spielerübersicht scrollen"><Overview header={header} ownership={ownership} moderation={moderation} profile={profile} onNavigate={setActiveTab} /></main></div>
                                    </TabsVerticalContent>
                                    <TabsVerticalContent value="ownership" className="fc-dossier-panel">
                                        <div className="fc-dossier-workspace is-direct">
                                            <main className="fc-dossier-primary" tabIndex={0} aria-label="Besitz scrollen">{loadedScopes.ownership !== activeScope && ownershipQuery.loading ? <LoadingBlock label="Besitz wird geladen …" /> : <><DomainFailure message={loadedScopes.ownership === activeScope ? undefined : ownershipQuery.error} onRetry={retryAll} /><DomainFailure message={catalogQuery.error} onRetry={retryCatalog} />{Object.keys(ownership).length > 0 && <Ownership data={ownership} target={selectedTarget} catalog={catalog} onRetry={retryAll} onSuccess={reloadDisability} />}</>}</main>
                                        </div>
                                    </TabsVerticalContent>
                                    <TabsVerticalContent value="moderation" className="fc-dossier-panel">
                                        <div className="fc-dossier-workspace is-direct">
                                            <main className="fc-dossier-primary" tabIndex={0} aria-label="Moderation scrollen">{loadedScopes.moderation !== activeScope && moderationQuery.loading ? <LoadingBlock label="Moderationsverlauf wird geladen …" /> : <><DomainFailure message={loadedScopes.moderation === activeScope ? undefined : moderationQuery.error} onRetry={retryAll} />{Object.keys(moderation).length > 0 && <Moderation data={moderation} />}</>}</main>
                                        </div>
                                    </TabsVerticalContent>
                                    <TabsVerticalContent value="administration" className="fc-dossier-panel">
                                        <div className="fc-dossier-workspace is-direct">
                                            <main className="fc-dossier-primary" tabIndex={0} aria-label="Verwaltung scrollen">{loadedScopes.administration !== activeScope && administrationQuery.loading ? <LoadingBlock label="Verwaltung wird geladen …" /> : <><DomainFailure message={loadedScopes.administration === activeScope ? undefined : administrationQuery.error} onRetry={retryAll} /><DomainFailure message={catalogQuery.error} onRetry={retryCatalog} />{Object.keys(administration).length > 0 && <Administration data={administration} catalog={catalog} />}</>}</main>
                                        </div>
                                    </TabsVerticalContent>
                                </div>
                            </TabsVertical>
                        </DossierMutationProvider>
                    ) : null}
                </DialogContent>
            </Dialog>
        </PlayerDossierContext.Provider>
    );
}

export const usePlayerDossier = () => {
    const value = useContext(PlayerDossierContext);
    if (!value) throw new Error('PlayerDossierProvider fehlt.');
    return value;
};
